interface VersionInfo {
    tag_name: string;
    name: string;
    body: string;
    published_at: string;
    html_url: string;
    assets: Array<{
        name: string;
        browser_download_url: string;
        server_download_url?: string;
    }>;
}

interface DownloadUrls {
    windows: string;
    mac: string;
    linux: string;
}

class VersionService {
    private latestVersion: VersionInfo | null = null;
    private lastFetchTime: number = 0;
    private readonly cacheTime = 5 * 60 * 1000; // 5分钟缓存

    /**
     * 获取最新版本信息
     */
    async getLatestVersion(): Promise<VersionInfo | null> {
        const now = Date.now();

        // 如果有缓存且未过期，直接返回缓存
        if (this.latestVersion && (now - this.lastFetchTime) < this.cacheTime) {
            return this.latestVersion;
        }

        try {
            const isDevEnv = typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';
            const isLocalHost = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|0\.0\.0\.0|192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(window.location.hostname);
            const base = (isDevEnv || isLocalHost)
                ? '/api'
                : (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) || 'https://note.name666.top/api';
            const response = await fetch(`${base}/app/version/latest`);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const versionInfo: VersionInfo = await response.json();

            // 更新缓存
            this.latestVersion = versionInfo;
            this.lastFetchTime = now;

            return versionInfo;
        } catch (error) {
            return null;
        }
    }

    /**
     * 获取下载链接
     */
    async getDownloadUrls(): Promise<{ github: DownloadUrls; server: DownloadUrls } | null> {
        const versionInfo = await this.getLatestVersion();

        if (!versionInfo) {
            return null;
        }

        // 从版本信息中提取下载链接
        const assets = versionInfo.assets || [];

        const githubUrls: DownloadUrls = {
            windows: assets.find(asset =>
                asset.name.toLowerCase().includes('windows') ||
                asset.name.toLowerCase().includes('win') ||
                asset.name.toLowerCase().includes('.exe')
            )?.browser_download_url || '',

            mac: assets.find(asset =>
                asset.name.toLowerCase().includes('macos') ||
                asset.name.toLowerCase().includes('mac') ||
                asset.name.toLowerCase().includes('.dmg')
            )?.browser_download_url || '',

            linux: assets.find(asset =>
                asset.name.toLowerCase().includes('linux') ||
                asset.name.toLowerCase().includes('.appimage')
            )?.browser_download_url || ''
        };

        // 服务器下载链接（备用）- 从API获取
        const serverBase = 'https://note.name666.top';
        const serverUrls: DownloadUrls = {
            windows: assets.find(asset =>
                asset.name.toLowerCase().includes('windows') ||
                asset.name.toLowerCase().includes('win') ||
                asset.name.toLowerCase().includes('.exe')
            )?.server_download_url || `${serverBase}/downloads/xiaoku-notes-windows.exe`,

            mac: assets.find(asset =>
                asset.name.toLowerCase().includes('macos') ||
                asset.name.toLowerCase().includes('mac') ||
                asset.name.toLowerCase().includes('.dmg')
            )?.server_download_url || `${serverBase}/downloads/xiaoku-notes-macos.dmg`,

            linux: assets.find(asset =>
                asset.name.toLowerCase().includes('linux') ||
                asset.name.toLowerCase().includes('.appimage')
            )?.server_download_url || `${serverBase}/downloads/xiaoku-notes-linux.AppImage`
        };

        return {
            github: githubUrls,
            server: serverUrls
        };
    }

    /**
     * 获取版本号
     */
    async getVersionNumber(): Promise<string> {
        const versionInfo = await this.getLatestVersion();
        return versionInfo?.tag_name || 'v1.0.0';
    }

    /**
     * 获取发布说明
     */
    async getReleaseNotes(): Promise<string> {
        const versionInfo = await this.getLatestVersion();
        return versionInfo?.body || '暂无发布说明';
    }

    /**
     * 获取发布页面链接
     */
    async getReleasePageUrl(): Promise<string> {
        const versionInfo = await this.getLatestVersion();
        return versionInfo?.html_url || 'https://github.com/your-username/xiaoku-notes/releases';
    }

    /**
     * 清除缓存
     */
    clearCache(): void {
        this.latestVersion = null;
        this.lastFetchTime = 0;
    }

    /**
     * 检测用户操作系统
     */
    detectOS(): 'windows' | 'mac' | 'linux' {
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.includes('windows')) return 'windows';
        if (userAgent.includes('mac')) return 'mac';
        if (userAgent.includes('linux')) return 'linux';
        return 'windows'; // 默认
    }

    /**
     * 获取推荐的下载链接
     */
    async getRecommendedDownloadUrl(): Promise<string> {
        const downloadUrls = await this.getDownloadUrls();
        if (!downloadUrls) {
            return 'https://github.com/your-username/xiaoku-notes/releases';
        }

        const os = this.detectOS();

        // 优先使用GitHub链接，如果没有则使用服务器链接
        return downloadUrls.github[os] || downloadUrls.server[os] || downloadUrls.github.windows;
    }

    /**
     * 获取所有平台的下载链接（用于下拉菜单）
     */
    async getAllDownloadOptions(): Promise<Array<{
        key: string;
        label: string;
        platform: 'windows' | 'mac' | 'linux';
        source: 'github' | 'server';
        url: string;
    }>> {
        const downloadUrls = await this.getDownloadUrls();
        if (!downloadUrls) {
            return [];
        }

        const options = [];

        // Windows选项
        if (downloadUrls.github.windows) {
            options.push({
                key: 'windows-github',
                label: 'Windows版 (GitHub)',
                platform: 'windows' as const,
                source: 'github' as const,
                url: downloadUrls.github.windows
            });
        }
        if (downloadUrls.server.windows) {
            options.push({
                key: 'windows-server',
                label: 'Windows版 (服务器)',
                platform: 'windows' as const,
                source: 'server' as const,
                url: downloadUrls.server.windows
            });
        }

        // macOS选项
        if (downloadUrls.github.mac) {
            options.push({
                key: 'mac-github',
                label: 'macOS版 (GitHub)',
                platform: 'mac' as const,
                source: 'github' as const,
                url: downloadUrls.github.mac
            });
        }
        if (downloadUrls.server.mac) {
            options.push({
                key: 'mac-server',
                label: 'macOS版 (服务器)',
                platform: 'mac' as const,
                source: 'server' as const,
                url: downloadUrls.server.mac
            });
        }

        // Linux选项
        if (downloadUrls.github.linux) {
            options.push({
                key: 'linux-github',
                label: 'Linux版 (GitHub)',
                platform: 'linux' as const,
                source: 'github' as const,
                url: downloadUrls.github.linux
            });
        }
        if (downloadUrls.server.linux) {
            options.push({
                key: 'linux-server',
                label: 'Linux版 (服务器)',
                platform: 'linux' as const,
                source: 'server' as const,
                url: downloadUrls.server.linux
            });
        }

        return options;
    }
}

// 创建单例
const versionService = new VersionService();

export default versionService;
export type { VersionInfo, DownloadUrls };
